import { useEffect, useRef, type CSSProperties } from 'react';

/**
 * The hero's right-panel showcase: a TWO-LANE CONVEYOR of the product's eight
 * real deliverable cards over the dark heartwood stage, with a bottom label.
 *
 * Motion (JS rAF loop below): cards ride an UPPER lane (~24% height) and a LOWER
 * lane (~66% height) — that's the scattered two-row layout — drifting right to
 * left. As each card crosses center it RISES into the focus slot (50%/50%),
 * scaling to full size, sharpening, and becoming fully readable (it sits in the
 * gradient's green spotlight), then settles back into its lane and drifts off.
 * Every card takes its turn as the readable "hero" card. ~4-5 cards visible at
 * once (2 per lane + the rising one). Cards enter already inset from the right
 * edge (a gutter), so none ever touches the viewport edge. Transform + opacity
 * (+ a capped blur on non-focus cards) only. Hover pauses; below 1025px and
 * reduced-motion fall back to a single centered card (CSS `:first-child`).
 *
 * Each card fills its lower half with a signature numbered FIGURE (a "figure in
 * a dossier" — hairline divider, mono `FIG. N` caption, one accent color each)
 * so no card has dead space and each is memorable:
 *   1 MARKET SIZE       — TAM/SAM/SOM bullseye
 *   2 FIELD INTERVIEWS  — Q/A exchange + interview tally
 *   3 PATENTABILITY     — novel / non-obvious / useful checks
 *   4 PRIOR-ART LANDSCAPE — scatter with the idea (spark dot) in an open gap
 *   5 EXPLODED VIEW     — patent-drawing line-art schematic
 *   6 EBITDA            — 5-year ascending bars + breakeven + spark trend
 *   7 THE CANVAS        — 3×3 business-model poster
 *   8 YOUR SHORTLIST    — anonymized attorney match grid + match meter
 */

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
};

/** Per-card lane index (kept for potential styling hooks). */
const slot = (i: number): CSSProperties => ({ ['--i' as string]: i }) as CSSProperties;

/** A brass checkmark used in the patentability figure. */
function Check() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

export function HeroShowcase({ staticSheet = false }: { staticSheet?: boolean } = {}) {
  const carouselRef = useRef<HTMLDivElement | null>(null);

  // Two-lane conveyor with a traveling spotlight (desktop only). Positions every
  // card each frame as a function of its horizontal phase: it drifts along its
  // lane, then rises into the center focus (readable) as it nears center.
  useEffect(() => {
    // `staticSheet` renders the eight cards as a flat, motionless grid for
    // export (see /admin/card-sheet). The conveyor writes inline transforms and
    // opacity every frame, so it has to be skipped entirely rather than
    // overridden in CSS.
    if (staticSheet) return;
    const carousel = carouselRef.current;
    if (!carousel) return;
    const cards = Array.from(
      carousel.querySelectorAll<HTMLElement>('.hero-cardpos'),
    );
    if (!cards.length) return;

    const CYCLE = 28; // seconds for one card to cross the whole stage
    // Fixed per-card jitter so the two lanes read hand-scattered, not ruler-straight.
    const jitterY = [6, -8, 4, -6, 8, -4, 6, -8];
    const jitterTilt = [1.2, -1, 0.8, -1.4, 1, -0.8, 1.2, -1];

    const mqDesktop = window.matchMedia('(min-width: 1025px)');
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    let raf = 0;
    let elapsed = 0; // seconds; advances only while not paused
    let last = 0;
    let paused = false;

    const clearInline = () => {
      for (const el of cards) {
        el.style.transform = '';
        el.style.opacity = '';
        el.style.filter = '';
        el.style.zIndex = '';
        el.style.left = '';
        el.style.top = '';
      }
    };

    // Cached geometry. The carousel and card dimensions change only when the
    // viewport/container resizes — never frame-to-frame — so we measure them
    // OFF the animation loop (on setup and via ResizeObserver) and read the
    // cached values inside frame(). Previously frame() read clientWidth /
    // offsetWidth every frame, right after the prior frame wrote transforms to
    // the cards; that invalidated layout each time and forced a synchronous
    // reflow on every animation frame (~229ms of forced reflow over the initial
    // load). Caching removes the per-frame reflow entirely and leaves the loop
    // write-only.
    let w = 0;
    let h = 0;
    let cardW = 0;
    let cardH = 0;
    const measure = () => {
      w = carousel.clientWidth;
      h = carousel.clientHeight;
      cardW = cards[0].offsetWidth;
      cardH = cards[0].offsetHeight;
    };

    const frame = (time: number) => {
      if (!w || !h || !cardW) return;
      const desktop = mqDesktop.matches;

      if (desktop) {
        // ===== DESKTOP: two-lane scatter with a traveling spotlight. Tiny gutters
        // → cards traverse nearly the FULL panel width; even-index cards ride a
        // high upper lane, odd a low lane, each rising to a center focus. =====
        const rightGutter = Math.max(8, w * 0.008);
        const leftGutter = Math.max(6, w * 0.006);
        const enterCX = w - rightGutter - cardW / 2;
        const exitCX = leftGutter + cardW / 2;
        const travel = Math.max(1, enterCX - exitCX);
        const focusX = exitCX + 0.52 * travel;
        const focusRange = 0.48 * travel;
        // Lane Y-fractions shifted DOWN (founder: "the conveyor cards sit too high
        // compared to the text"). The card mass used to skew to the upper portion
        // (laneUp 0.06, laneDown 0.7, focus 0.4), leaving the bottom of the column
        // empty so the whole cascade read HIGH. Now the two lanes bracket center
        // more symmetrically (0.15 / 0.8, midpoint ~0.47) and the readable focus
        // card sits at the vertical CENTER (0.5), so the cascade fills the column
        // evenly and lines up with the text block's vertical center. Bounds check
        // (h≈558 at 62vh/900, card 368 → lane scale 0.6 half≈110, focus half≈184):
        // laneDown 0.8h+110 ≈ 556 ≤ h; focus 0.5h±184 ≈ 95..463 within h.
        const laneUp = 0.15 * h;
        const laneDown = 0.8 * h;
        const focusY = 0.5 * h;

        for (let i = 0; i < cards.length; i++) {
          const el = cards[i];
          const phase = ((time / CYCLE) + i / cards.length) % 1;
          const cx = enterCX - phase * travel;
          const laneY = (i % 2 === 0 ? laneUp : laneDown) + jitterY[i % jitterY.length];
          const baseTilt = (i % 2 === 0 ? 2.5 : -2.5) + jitterTilt[i % jitterTilt.length];
          const d = clamp01(1 - Math.abs(cx - focusX) / focusRange);
          const e = d * d * (3 - 2 * d);
          const scale = lerp(0.6, 1, e);
          const cy = lerp(laneY, focusY, e);
          const rot = lerp(baseTilt, 0, e);
          const z = Math.round(lerp(10, 40, e));
          const blur = lerp(1.2, 0, e);
          const edge = smoothstep(0.1, 0.22, phase) * (1 - smoothstep(0.85, 0.96, phase));
          const opacity = lerp(0.52, 1, e) * edge;
          el.style.left = '0';
          el.style.top = '0';
          el.style.transform = `translate(${(cx - cardW / 2).toFixed(1)}px, ${(cy - cardH / 2).toFixed(1)}px) scale(${scale.toFixed(3)}) rotate(${rot.toFixed(2)}deg)`;
          el.style.opacity = opacity.toFixed(3);
          el.style.filter = blur > 0.06 ? `blur(${blur.toFixed(2)}px)` : 'none';
          el.style.zIndex = String(z);
        }
        return;
      }

      // ===== MOBILE: a SINGLE-LANE spotlight (the narrow panel can't hold two
      // lanes). Cards ride one central lane, sliding right→left; each grows to
      // full size dead-center (readable), one focused at a time. Tighter focus
      // range so ~1-2 cards show. Faster cycle so it doesn't feel sluggish. =====
      const mCycle = 18; // 8 cards → a new card focuses ~every 2.25s
      const enterCX = w + cardW * 0.5; // enters just off the right edge
      const exitCX = -cardW * 0.5; // exits just off the left edge
      const travel = enterCX - exitCX;
      const focusX = w * 0.5; // dead-center
      const focusRange = w * 0.42;
      const centerY = h * 0.5;

      for (let i = 0; i < cards.length; i++) {
        const el = cards[i];
        const phase = ((time / mCycle) + i / cards.length) % 1;
        const cx = enterCX - phase * travel;
        const baseTilt = i % 2 === 0 ? 2.5 : -2.5;
        const d = clamp01(1 - Math.abs(cx - focusX) / focusRange);
        const e = d * d * (3 - 2 * d);
        const scale = lerp(0.78, 1, e);
        const rot = lerp(baseTilt, 0, e);
        const z = Math.round(lerp(10, 40, e));
        const blur = lerp(1.4, 0, e);
        // Only the card near center is opaque; neighbors fade at the edges so a
        // single readable card leads on the small screen.
        const edge = smoothstep(0.04, 0.16, phase) * (1 - smoothstep(0.84, 0.96, phase));
        const opacity = lerp(0.28, 1, e) * edge;
        el.style.left = '0';
        el.style.top = '0';
        el.style.transform = `translate(${(cx - cardW / 2).toFixed(1)}px, ${(centerY - cardH / 2).toFixed(1)}px) scale(${scale.toFixed(3)}) rotate(${rot.toFixed(2)}deg)`;
        el.style.opacity = opacity.toFixed(3);
        el.style.filter = blur > 0.06 ? `blur(${blur.toFixed(2)}px)` : 'none';
        el.style.zIndex = String(z);
      }
    };

    const tick = (now: number) => {
      if (!last) last = now;
      const dt = Math.min((now - last) / 1000, 0.05); // clamp long frames (tab switch)
      last = now;
      if (!paused) elapsed += dt;
      frame(elapsed);
      raf = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      last = 0;
    };

    const setup = () => {
      stop();
      if (mqReduce.matches) {
        // Reduced motion (any width): clear inline styles → CSS fallback shows a
        // single static centered card; no animation.
        clearInline();
        return;
      }
      // The conveyor now runs at ALL widths — two-lane on desktop, single-lane
      // spotlight on mobile/tablet (branched inside frame()).
      measure(); // seed cached geometry once before the (write-only) loop starts
      raf = requestAnimationFrame(tick);
    };

    const onEnter = () => {
      paused = true;
    };
    const onLeave = () => {
      paused = false;
    };
    carousel.addEventListener('pointerenter', onEnter);
    carousel.addEventListener('pointerleave', onLeave);
    mqDesktop.addEventListener('change', setup);
    mqReduce.addEventListener('change', setup);
    // Re-measure cached geometry only when the carousel actually resizes. The
    // callback runs off the animation loop (a handful of times, not per frame),
    // so the layout read here doesn't reintroduce per-frame reflow.
    const ro = new ResizeObserver(() => measure());
    ro.observe(carousel);
    setup();

    return () => {
      stop();
      ro.disconnect();
      carousel.removeEventListener('pointerenter', onEnter);
      carousel.removeEventListener('pointerleave', onLeave);
      mqDesktop.removeEventListener('change', setup);
      mqReduce.removeEventListener('change', setup);
    };
  }, [staticSheet]);

  return (
    <div className={`hero-showcase${staticSheet ? ' hero-showcase--sheet' : ''}`}>
      <div className="hero-showcase__carousel" aria-hidden="true" ref={carouselRef}>
        {/* 1 — market-viability memo · FIG.1 TAM/SAM/SOM bullseye */}
        <div className="hero-cardpos" style={slot(0)}>
          <div className="hero-card hero-card--market">
            <span className="hero-card__arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M6 13l6 6 6-6" />
              </svg>
            </span>
            <div className="hero-card__top">
              <span className="hero-card__step">SPARK · VALIDATE</span>
              <span className="hero-card__id">DISC-2026-05-02</span>
            </div>
            <div className="hero-card__title">A market-viability memo</div>
            <span className="hero-card__pill">
              <span className="hero-card__pill-dot" aria-hidden="true" />
              Promising
            </span>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 1 — MARKET SIZE</div>
              <div className="hero-fig__win hero-fig__bull">
                <div className="hero-fig__rings" aria-hidden="true">
                  <span className="r-tam" />
                  <span className="r-sam" />
                  <span className="r-som" />
                </div>
                <div className="hero-fig__legend">
                  <div><i className="k-tam" />TAM $2.4B</div>
                  <div><i className="k-sam" />SAM $610M</div>
                  <div><i className="k-som" />SOM $48M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2 — customer discovery kit · FIG.2 field interviews */}
        <div className="hero-cardpos" style={slot(1)}>
          <div className="hero-card hero-card--kit">
            <div className="hero-card__top">
              <span className="hero-card__step">SPARK · VALIDATE</span>
              <span className="hero-card__id">DISC-2026-05-02</span>
            </div>
            <div className="hero-card__title">A customer discovery kit</div>
            <span className="hero-card__pill hero-card__pill--brass">
              <span className="hero-card__pill-dot" aria-hidden="true" />
              Mom Test
            </span>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 2 — FIELD INTERVIEWS</div>
              <div className="hero-fig__win hero-fig__qa">
                <span className="qa-q">Who else has this problem?</span>
                <span className="qa-a">&ldquo;Every single shift.&rdquo;</span>
                <div className="qa-tally">
                  <b aria-hidden="true" />
                  12 interviews grounded in your memo
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3 — invention strategy memo · Go. verdict + seal + FIG.3 checks */}
        <div className="hero-cardpos" style={slot(2)}>
          <div className="hero-card hero-card--memo">
            <div className="hero-card__seal" aria-hidden="true">
              <span>SEAL</span>
            </div>
            <div className="hero-card__step">STEP 02 · INVENT</div>
            <div className="hero-card__title">An invention strategy memo</div>
            <div className="hero-card__verdict">Go.</div>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 3 — PATENTABILITY</div>
              <div className="hero-fig__win hero-fig__checks">
                <div><Check />Novel</div>
                <div><Check />Non-obvious</div>
                <div><Check />Useful</div>
                <div className="checks-note">3 claims drafted · attorney-ready</div>
              </div>
            </div>
          </div>
        </div>

        {/* 4 — prior-art search plan · FIG.4 landscape scatter */}
        <div className="hero-cardpos" style={slot(3)}>
          <div className="hero-card hero-card--search">
            <div className="hero-card__top">
              <span className="hero-card__step">STEP 02 · INVENT</span>
              <span className="hero-card__id">PA-2026-05-02</span>
            </div>
            <div className="hero-card__title">A prior-art search plan</div>
            <div className="hero-card__query">(&ldquo;wrist rest&rdquo; OR &ldquo;wrist support&rdquo;) AND…</div>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 4 — PRIOR-ART LANDSCAPE</div>
              <div className="hero-fig__win hero-fig__scatter" aria-hidden="true">
                <span className="pa-dot" style={{ left: '14%', top: '26%' }} />
                <span className="pa-dot" style={{ left: '26%', top: '52%' }} />
                <span className="pa-dot" style={{ left: '20%', top: '76%' }} />
                <span className="pa-dot" style={{ left: '38%', top: '34%' }} />
                <span className="pa-dot" style={{ left: '44%', top: '64%' }} />
                <span className="pa-dot" style={{ left: '34%', top: '84%' }} />
                <span className="pa-dot" style={{ left: '52%', top: '44%' }} />
                <span className="pa-gap" style={{ right: '14%', bottom: '20%' }} />
                <span className="pa-mine" style={{ right: '21%', bottom: '30%' }} />
                <span className="pa-mine-label">your idea</span>
              </div>
            </div>
          </div>
        </div>

        {/* 5 — buildable design breakdown · FIG.5 exploded view */}
        <div className="hero-cardpos" style={slot(4)}>
          <div className="hero-card hero-card--design">
            <div className="hero-card__top">
              <span className="hero-card__step">STEP 03 · BUILD</span>
              <span className="hero-card__id">DSGN-2026-05-02</span>
            </div>
            <div className="hero-card__title">A buildable design breakdown</div>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 5 — EXPLODED VIEW · 7 PARTS</div>
              <div className="hero-fig__win hero-fig__exploded">
                <svg viewBox="0 0 150 96" fill="none" aria-hidden="true">
                  <rect x="46" y="8" width="58" height="16" rx="3" />
                  <rect x="46" y="34" width="58" height="16" rx="3" />
                  <ellipse cx="75" cy="70" rx="34" ry="11" />
                  <line x1="46" y1="16" x2="20" y2="16" />
                  <circle cx="14" cy="16" r="6" />
                  <text x="14" y="19" className="ex-n">1</text>
                  <line x1="104" y1="42" x2="132" y2="42" />
                  <circle cx="138" cy="42" r="6" />
                  <text x="138" y="45" className="ex-n">2</text>
                  <line x1="75" y1="81" x2="75" y2="92" />
                  <circle cx="75" cy="90" r="6" />
                  <text x="75" y="93" className="ex-n">3</text>
                </svg>
                <div className="ex-cap">Air bladder · heating film · 12 materials</div>
              </div>
            </div>
          </div>
        </div>

        {/* 6 — 5-year business case · FIG.6 EBITDA chart */}
        <div className="hero-cardpos" style={slot(5)}>
          <div className="hero-card hero-card--case">
            <div className="hero-card__top">
              <span className="hero-card__step">STEP 03 · BUILD</span>
              <span className="hero-card__id">CASE-2026-05-02</span>
            </div>
            <div className="hero-card__title">A 5-year business case</div>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 6 — EBITDA · $18M–$42M</div>
              <div className="hero-fig__win hero-fig__ebitda">
                <svg viewBox="0 0 150 76" fill="none" aria-hidden="true">
                  {/* breakeven dotted line */}
                  <line x1="4" y1="48" x2="146" y2="48" className="be-line" />
                  {/* bars Y1-Y5, Y3 (breakeven) in brass */}
                  <rect x="10" y="56" width="18" height="14" className="bar" />
                  <rect x="38" y="46" width="18" height="24" className="bar" />
                  <rect x="66" y="34" width="18" height="36" className="bar bar--be" />
                  <rect x="94" y="22" width="18" height="48" className="bar" />
                  <rect x="122" y="10" width="18" height="60" className="bar" />
                  {/* spark trend line over the bar tops */}
                  <polyline points="19,56 47,46 75,34 103,22 131,10" className="trend" />
                </svg>
                <div className="ebitda-axis">
                  <span>Y1</span><span>Y2</span><span>Y3</span><span>Y4</span><span>Y5</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 7 — business model canvas · FIG.7 the canvas */}
        <div className="hero-cardpos" style={slot(6)}>
          <div className="hero-card hero-card--canvas">
            <div className="hero-card__top">
              <span className="hero-card__step">STEP 03 · BUILD</span>
              <span className="hero-card__id">BMC-2026-05-02</span>
            </div>
            <div className="hero-card__title">A business model canvas</div>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 7 — THE CANVAS · 9 BLOCKS</div>
              <div className="hero-fig__win hero-fig__canvas" aria-hidden="true">
                <i /><i /><i />
                <i /><i className="c-value" /><i />
                <i /><i /><i />
              </div>
            </div>
          </div>
        </div>

        {/* 8 — vetted attorney match · FIG.8 shortlist */}
        <div className="hero-cardpos" style={slot(7)}>
          <div className="hero-card hero-card--match">
            <div className="hero-card__top">
              <span className="hero-card__step">STEP 04 · FILE</span>
              <span className="hero-card__id">MATCH-2026</span>
            </div>
            <div className="hero-card__title">A vetted attorney match</div>
            <div className="hero-card__profile">
              <span className="hero-card__avatar" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4 3.5-6 8-6s8 2 8 6" />
                </svg>
              </span>
              <div>
                <div className="hero-card__profile-role">Registered Patent Attorney</div>
                <div className="hero-card__profile-meta">Mechanical · Micro-entity OK</div>
              </div>
            </div>
            <div className="hero-fig">
              <div className="hero-fig__cap">FIG. 8 — YOUR SHORTLIST</div>
              <div className="hero-fig__win hero-fig__shortlist">
                <div className="sl-grid" aria-hidden="true">
                  <i /><i /><i className="sl-on" /><i /><i />
                  <i /><i /><i /><i /><i />
                </div>
                <div className="sl-meter" aria-hidden="true"><span /></div>
                <div className="sl-label">12 match your field · CA · Mechanical</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* The bottom-anchored caption label ("One spark-to-business journey /
          Eight deliverables / Your innovation OS, in one dashboard") was REMOVED
          at the founder's request. The right panel is now the pure deliverable
          conveyor, sized to fill the column so its top/bottom align with the
          left hero's headline top and microline bottom. */}
    </div>
  );
}
